<template>
  <div class="panel-root">
    <div class="page-container">
      <!-- 面包屑 -->
      <nav class="breadcrumb">
        <a class="bc-link" @click.prevent="goHome">首页</a>
        <span class="bc-sep">/</span>
        <span class="bc-link">{{ title }}</span>
        <span class="bc-sep">/</span>
        <span class="bc-current">选择{{ patientLabel }}</span>
      </nav>

      <!-- Hero 头部 -->
      <div class="hero-row">
        <div class="hero-left">
          <p class="hero-brand">关盈堂 · 健康服务</p>
          <h1 class="hero-title">为每一位{{ patientLabel }}，开启健康之旅</h1>
          <p class="hero-subtitle">选择{{ patientLabel }}档案，设定预测时间，让健康管理更有方向。</p>
        </div>
        <button class="hero-back" type="button" @click="$emit('back')">
          <span>返回我的服务</span>
          <span class="hero-back-arrow" aria-hidden="true">→</span>
        </button>
      </div>

      <!-- 步骤条 -->
      <ol class="steps">
        <li class="step is-active"><span class="step-dot">1</span><span class="step-text">选择{{ patientLabel }}</span></li>
        <li class="step-line" aria-hidden="true"></li>
        <li class="step"><span class="step-dot">2</span><span class="step-text">设置预测</span></li>
        <li class="step-line" aria-hidden="true"></li>
        <li class="step"><span class="step-dot">3</span><span class="step-text">查看结果</span></li>
      </ol>

      <!-- 左右双栏 -->
      <div class="layout">
        <!-- 左：选择患者档案 -->
        <section class="card card-patient">
          <div class="card-head">
            <div class="card-head-left">
              <p class="card-eyebrow">PATIENT RECORDS</p>
              <h2 class="card-title">
                选择{{ patientLabel }}档案
                <span class="card-count">{{ patientCount }} 位{{ patientLabel }}</span>
              </h2>
            </div>
            <button class="add-btn" type="button" @click="contactDialogVisible = true">
              <span class="add-btn-plus" aria-hidden="true">＋</span>
              <span>{{ patientAddButtonText }}</span>
            </button>
          </div>

          <div class="search-row">
            <div class="search-box">
              <span class="search-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#9aa8a3" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
              </span>
              <el-input v-model="searchText" class="search-input" :placeholder="patientSearchPlaceholder"
                @keyup.enter.native="search" />
            </div>
            <button class="search-btn" type="button" @click="search">搜索档案</button>
          </div>

          <div v-show="searchTableVisible" class="search-wrap">
            <el-table class="search-table" :data="searchArray" :show-header="true" max-height="360" width="100%"
              @row-click="tableItemClick">
              <el-table-column min-width="150" property="name" label="姓名" />
              <el-table-column min-width="150" property="phoneNo" label="电话" />
              <el-table-column min-width="200" label="出生日期">
                <template slot-scope="scope">
                  {{ scope.row.birthday }}
                  <span v-if="shouldShowTime(scope.row.birthHour, scope.row.birthMinute)">{{
                    formatTimeValue(scope.row.birthHour) }}:{{ formatTimeValue(scope.row.birthMinute) }}</span>
                </template>
              </el-table-column>
            </el-table>
            <el-pagination v-if="totalPage > 10" class="pagination" background layout="prev, pager, next"
              :current-page="currentPageNo" @current-change="pageChange" :total="totalPage" />
          </div>

          <div v-if="selectContactVisible" class="contact-card">
            <div class="cc-head">
              <span class="cc-avatar">{{ avatarText }}</span>
              <div class="cc-head-info">
                <span class="cc-name">{{ selectContact.name }}</span>
                <span class="cc-meta">
                  <template v-if="!isEmptyValue(selectContact.sex)">{{ sexText }}</template>
                  <template v-if="!isEmptyValue(selectContact.birthday)"><span class="cc-dot">·</span>{{
                    selectContact.birthday }} 出生</template>
                </span>
              </div>
              <span class="cc-tag">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="3"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                已选择{{ patientLabel }}
              </span>
            </div>
            <div class="cc-rows">
              <div v-if="!isEmptyValue(selectContact.liveArea)" class="cc-row"><span class="ck">常住地址</span><span
                  class="cv">{{ selectContact.liveArea }}</span></div>
              <div v-if="!isEmptyValue(selectContact.birthArea)" class="cc-row"><span class="ck">出生地址</span><span
                  class="cv">{{ selectContact.birthArea }}</span></div>
            </div>
          </div>

          <p class="card-hint">{{ patientLabel }}档案仅用于健康服务，信息以档案记录为准。</p>
        </section>

        <!-- 右：设置面板 -->
        <aside class="card card-setting">
          <div class="setting-head">
            <p class="card-eyebrow eyebrow-light">HEALTH PREDICTION</p>
            <h2 class="setting-title">{{ title }}</h2>
            <p class="setting-sub">{{ title }}设置</p>
            <p class="setting-desc">{{ subtitle }}</p>
          </div>

          <div class="setting-body">
            <div class="current-patient">
              <span class="cp-label">{{ currentLabel }}</span>
              <span v-if="hasSelectedContact" class="cp-value">{{ selectContact.name }}
                <em class="cp-sex">{{ sexText }}</em></span>
              <span v-else class="cp-value cp-empty">未选择</span>
            </div>

            <!-- 月方/日方/时方 -->
            <div v-if="ganZhiRadioVisible" class="ganZhi-group">
              <el-radio-group v-model="ganZhiPrescriptionTimeType" size="large" @change="onPrescriptionTypeChange">
                <el-radio-button label="monthly">{{ monthLabel }}</el-radio-button>
                <el-radio-button label="daily">{{ dayLabel }}</el-radio-button>
                <el-radio-button label="hourly">{{ hourLabel }}</el-radio-button>
              </el-radio-group>
              <div v-if="ganZhiPrescriptionTimeType === 'monthly'" class="prescription-tip">以月/半月为周期</div>
              <div v-else-if="ganZhiPrescriptionTimeType === 'daily'" class="prescription-tip">以日为周期</div>
              <div v-else-if="ganZhiPrescriptionTimeType === 'hourly'" class="prescription-tip">以时辰为周期</div>
            </div>

            <div class="field-wrap">
              <label class="field-label">{{ dateLabel }}</label>
              <div class="field">
                <span class="field-icon"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#8aa09b"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </svg></span>
                <el-date-picker class="field-el" v-model="useDate" size="large" @change="useDateChange" :editable="false"
                  :picker-options="useDatePickerOptions" prefix-icon="clear" :placeholder="datePlaceholder"
                  format="yyyy-MM-dd" value-format="yyyy-MM-dd" type="date" />
              </div>
            </div>

            <div v-if="illDayVisible" class="field-wrap">
              <label class="field-label">发病日期</label>
              <div class="field">
                <span class="field-icon"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#8aa09b"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
                  </svg></span>
                <el-date-picker class="field-el" v-model="illDay" size="large" :picker-options="illPickerOptions"
                  :editable="false" prefix-icon="clear" :placeholder="illDayPlaceholder" format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd" type="date" />
              </div>
            </div>

            <div v-if="daysFieldVisible" class="field-wrap">
              <label class="field-label">{{ daysLabel }}</label>
              <div class="field">
                <span class="field-icon"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#8aa09b"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="9" width="18" height="6" rx="3" />
                    <path d="M9 9v6M15 9v6" />
                  </svg></span>
                <el-input class="field-el" @wheel.native.prevent v-model="useDays" :placeholder="dynamicDayPlaceholder"
                  type="number" />
                <span class="field-suffix">天</span>
              </div>
              <div class="chips">
                <button v-for="d in quickDays" :key="d" type="button" class="chip"
                  :class="{ active: isQuickDayActive(d) }" @click="applyQuickDays(d)">{{ d }}天</button>
              </div>
            </div>

            <div v-if="hourRangeVisible" class="field-wrap">
              <label class="field-label">时段</label>
              <div class="field field-select">
                <span class="field-icon"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#8aa09b"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg></span>
                <el-select class="field-el" v-model="ganZhiPrescriptionHourRange" placeholder="请选择时段">
                  <el-option v-for="item in timeRangeOptions" :key="item.value" :label="item.label"
                    :value="item.value" />
                </el-select>
              </div>
            </div>

            <button class="submit-btn" type="button" @click="onSubmit">
              <span>{{ submitButtonText }}</span>
              <span class="submit-arrow" aria-hidden="true">→</span>
            </button>
            <p class="card-hint setting-hint">结果基于传统健康模型，不作为医学诊断或治疗依据。</p>
          </div>
        </aside>
      </div>

      <!-- 页脚 -->
      <footer class="page-footer">
        <span>专业中医服务 · 守护健康人生</span>
        <span>一人一档，让关怀更精准</span>
      </footer>
    </div>

    <patient-form-dialog :visible.sync="contactDialogVisible" mode="add" :dialog-width="dialogWidth"
      :address-required="addressRequired" :add-title="patientAddDialogTitle" @success="onPatientFormSuccess"
      @success-multi="onPatientFormSuccessMulti" />
  </div>
</template>

<script>
import PatientFormDialog from "./PatientFormDialog.vue";

export default {
  name: "ConsultContactPanel",
  components: { PatientFormDialog },
  props: {
    title: { type: String, default: "自拟组方" },
    showVerticalLabel: { type: Boolean, default: false },
    datePlaceholder: { type: String, default: "请选择开始服药日期" },
    dayPlaceholder: { type: String, default: "请输入服药天数（不大于30天）" },
    showUseDays: { type: Boolean, default: true },
    maxUseDays: { type: Number, default: 30 },
    showGanZhiControls: { type: Boolean, default: false },
    showIllDay: { type: Boolean, default: false },
    submitButtonText: { type: String, default: "开始咨询" },
    addressRequired: { type: Boolean, default: true },
    dialogWidth: { type: String, default: "600px" },
    initialContact: { type: Object, default: null },
    initialUseDate: { type: String, default: "" },
    initialUseDays: { type: String, default: "" },
    initialIllDay: { type: String, default: "" },
    initialGanZhiPrescriptionTimeType: { type: String, default: "" },
    initialGanZhiPrescriptionHourRange: { type: String, default: "" },
    futureDateMessage: { type: String, default: "开方日期必须大于当前日期" },
    patientSectionTitle: { type: String, default: "患者信息" },
    patientSearchPlaceholder: { type: String, default: "请输入患者姓名或出生日期" },
    patientAddButtonText: { type: String, default: "新增患者" },
    patientAddDialogTitle: { type: String, default: "" },
    illDayPlaceholder: { type: String, default: "请选择发病日期" },
    dayPlaceholderMonthly: { type: String, default: "请输入服药天数（不大于30天）" },
    dayPlaceholderDaily: { type: String, default: "请输入服药天数（不大于3天）" },
    monthLabel: { type: String, default: "月方" },
    dayLabel: { type: String, default: "日方" },
    hourLabel: { type: String, default: "时方" }
  },
  data() {
    return {
      searchText: "",
      searchArray: [],
      searchTableVisible: false,
      selectContact: {},
      selectContactVisible: false,
      contactDialogVisible: false,
      useDate: "",
      illDay: "",
      useDays: "",
      ganZhiPrescriptionTimeType: "",
      ganZhiPrescriptionHourRange: "",
      useDaysPlaceholder: this.dayPlaceholder,
      totalPage: 1,
      currentPageNo: 1,
      pageSize: 5,
      quickDays: [7, 14, 30],
      timeRangeOptions: this.buildTimeRangeOptions()
    };
  },
  computed: {
    // 药食同源调理方案流程：使用"调理人/调理方案"等文案，避免出现患者/开方/服药等字眼
    isChaoweiFlow() { return this.title === "药食同源调理方案"; },
    patientLabel() { return this.isChaoweiFlow ? "调理人" : "患者"; },
    currentLabel() { return this.isChaoweiFlow ? "当前调理人" : "当前预测患者"; },
    dateLabel() { return this.isChaoweiFlow ? "调理开始日期" : "预测开始日期"; },
    daysLabel() { return this.isChaoweiFlow ? "调理持续天数" : "预测持续天数"; },
    hasSelectedContact() { return !this.isEmptyValue(this.selectContact.name); },
    sexText() {
      if (this.isEmptyValue(this.selectContact.sex)) return "";
      return this.selectContact.sex == "0" ? "女" : "男";
    },
    avatarText() {
      var n = this.selectContact.name;
      if (n && n.length > 0) return n.charAt(0);
      return this.patientLabel.charAt(0);
    },
    patientCount() {
      if (this.selectContactVisible && !this.isEmptyValue(this.selectContact.name)) return 1;
      return this.searchArray.length;
    },
    ganZhiRadioVisible() { return this.showGanZhiControls; },
    illDayVisible() { return this.showIllDay; },
    daysFieldVisible() {
      if (!this.showUseDays) return false;
      if (this.showGanZhiControls && this.ganZhiPrescriptionTimeType === "hourly") return false;
      return true;
    },
    hourRangeVisible() { return this.showGanZhiControls && this.ganZhiPrescriptionTimeType === "hourly"; },
    effectiveMaxUseDays() {
      if (this.showGanZhiControls && this.ganZhiPrescriptionTimeType === "daily") return 3;
      return this.maxUseDays;
    },
    dynamicDayPlaceholder() {
      if (this.showGanZhiControls && this.ganZhiPrescriptionTimeType === "daily") return this.dayPlaceholderDaily;
      if (this.showGanZhiControls && this.ganZhiPrescriptionTimeType === "monthly") return this.dayPlaceholderMonthly;
      return this.useDaysPlaceholder;
    },
    illPickerOptions() {
      return { disabledDate(time) { return time.getTime() > Date.now(); } };
    },
    useDatePickerOptions() {
      return {
        disabledDate(time) {
          const now = new Date();
          const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const end = new Date(start);
          end.setFullYear(end.getFullYear() + 2);
          return time.getTime() < start.getTime() || time.getTime() > end.getTime();
        }
      };
    }
  },
  mounted() {
    if (this.initialContact && Object.keys(this.initialContact).length > 0) {
      this.selectContact = this.initialContact;
      this.selectContactVisible = true;
    }
    if (this.initialUseDate) this.useDate = this.initialUseDate;
    if (this.initialUseDays) this.useDays = this.initialUseDays;
    if (this.initialIllDay) this.illDay = this.initialIllDay;
    if (this.initialGanZhiPrescriptionTimeType) this.ganZhiPrescriptionTimeType = this.initialGanZhiPrescriptionTimeType;
    if (this.initialGanZhiPrescriptionHourRange) this.ganZhiPrescriptionHourRange = this.initialGanZhiPrescriptionHourRange;
  },
  methods: {
    buildTimeRangeOptions() {
      const opts = [];
      for (let h = 0; h < 23; h++) {
        const s = String(h).padStart(2, "0");
        const e = String(h + 1).padStart(2, "0");
        opts.push({ label: `${s}:00-${e}:00`, value: `${s}:00-${e}:00` });
      }
      opts.push({ label: "23:00-24:00", value: "23:00-24:00" });
      return opts;
    },
    shouldShowTime(hour, minute) { return !(this.isEmptyValue(hour) && this.isEmptyValue(minute)); },
    isEmptyValue(v) { return v === "" || v === null || v === undefined; },
    formatTimeValue(v) { if (v === "-1" || v === -1 || v === "--") return "--"; return v; },
    goHome() { this.$router.replace("/"); },
    isQuickDayActive(d) { return String(this.useDays) === String(d); },
    applyQuickDays(d) { this.useDays = String(d); },
    onPatientFormSuccess(data) {
      this.selectContact = data;
      this.searchText = "";
      this.searchArray = [];
      this.searchTableVisible = false;
      this.selectContactVisible = true;
    },
    onPatientFormSuccessMulti(data) {
      this.searchTableVisible = true;
      this.selectContactVisible = false;
      this.searchArray = data;
      this.selectContact = {};
    },
    search() {
      const loading = this.$loading({ lock: true, text: "搜索中...", spinner: "el-icon-loading", background: "rgba(0, 0, 0, 0.1)" });
      this.wjtHttp.post(this, "/api/patient/v1/list", { name: this.searchText.trim(), pageNum: this.currentPageNo, pageSize: this.pageSize }, false,
        (res) => {
          const data = res.data;
          loading.close();
          if (data.code == 200) {
            this.totalPage = Math.ceil(data.total / 5) * 10;
            this.searchArray = data.rows;
            if (this.searchArray.length > 0) {
              this.searchTableVisible = true;
              this.selectContactVisible = false;
            } else {
              this.$message({ message: "未找到该" + this.patientLabel + "，请手动添加", type: "warning" });
            }
          } else {
            this.$message({ message: data.msg && data.msg.length > 0 ? data.msg : "搜索异常，请联系管理员", type: "error" });
          }
        },
        (err) => { console.log(err); loading.close(); }
      );
    },
    useDateChange(val) {
      const flag = this.utils.afterDateFromCurrent(val);
      const twoYearsFromNow = new Date();
      twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
      const yearAfter = new Date(val).getTime() > twoYearsFromNow.getTime();
      setTimeout(() => {
        if (!flag) { this.useDate = ""; this.$message({ message: this.futureDateMessage, type: "warning" }); }
        if (yearAfter) { this.useDate = ""; this.$message({ message: (this.isChaoweiFlow ? "调理" : "开方") + "日期必须在两年以内", type: "warning" }); }
      }, 100);
    },
    tableItemClick(row) {
      if (!this.shouldShowTime(row.birthHour, row.birthMinute)) {
        this.$confirm("该" + this.patientLabel + "未填写出生时辰，请前往补充", "提示", { confirmButtonText: "确定", cancelButtonText: "取消" })
          .then(() => { this.$router.push({ path: "/Contact", query: { editPatientId: row.patientId } }); })
          .catch(() => { });
        return;
      }
      this.searchTableVisible = false;
      this.selectContact = row;
    },
    pageChange(pageNo) { this.currentPageNo = pageNo; this.search(); },
    onPrescriptionTypeChange(val) {
      if (val === "hourly") { this.useDays = ""; this.useDate = ""; } else { this.ganZhiPrescriptionHourRange = ""; }
    },
    onSubmit() {
      if (this.utils.isEmptyObject(this.selectContact)) { this.$message({ message: "请先添加" + this.patientLabel + "信息", type: "error" }); return; }
      if (this.useDate.length == 0) { this.$message({ message: this.isChaoweiFlow ? "请选择开始调理日期" : "请选择使用日期", type: "error" }); return; }
      if (this.ganZhiRadioVisible && this.ganZhiPrescriptionTimeType.length == 0) {
        this.$message({ message: this.isChaoweiFlow ? "请选择月方案/日方案/时方案" : "请选择月方/日方/时方", type: "error" });
        return;
      }
      if (this.ganZhiRadioVisible && this.ganZhiPrescriptionTimeType === "hourly") {
        if (this.ganZhiPrescriptionHourRange.length == 0) { this.$message({ message: this.isChaoweiFlow ? "请选择服用时段" : "请选择时段", type: "error" }); return; }
      } else if (this.daysFieldVisible) {
        if (this.useDays.length == 0) { this.$message({ message: this.isChaoweiFlow ? "请输入调理天数" : "请输入使用天数", type: "error" }); return; }
        if (isNaN(parseInt(this.useDays))) { this.$message({ message: "请输入正确的天数", type: "error" }); return; }
        if (parseInt(this.useDays) <= 0) { this.$message({ message: this.isChaoweiFlow ? "调理天数不能小于1天" : "使用天数不能小于1天", type: "error" }); return; }
        if (parseInt(this.useDays) > this.effectiveMaxUseDays) { this.$message({ message: (this.isChaoweiFlow ? "调理天数" : "使用天数") + `不能大于${this.effectiveMaxUseDays}天`, type: "error" }); return; }
      }
      if (this.illDay.length == 0) {
        const d = new Date();
        this.illDay = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      }
      this.$emit("submit", {
        selectContact: this.selectContact,
        useDate: this.useDate,
        illDay: this.illDay,
        useDays: this.useDays,
        ganZhiPrescriptionTimeType: this.ganZhiPrescriptionTimeType,
        ganZhiPrescriptionHourRange: this.ganZhiRadioVisible && this.ganZhiPrescriptionTimeType === "hourly" ? this.ganZhiPrescriptionHourRange : ""
      });
    }
  },
  watch: {
    searchTableVisible(val) {
      if (!val && !this.utils.isEmptyObject(this.selectContact) && !this.utils.isEmptyStr(this.selectContact.name)) {
        this.selectContactVisible = true;
      }
    }
  }
};
</script>

<style scoped>
/* ===== 页面根容器 ===== */
.panel-root {
  position: relative;
  min-height: 100vh;
  background: #f5f7f2;
  overflow: auto;
  font-family: 'Noto Sans SC', system-ui, sans-serif;
  color: #304744;
  padding-bottom: 40px;
}

.page-container {
  max-width: 1220px;
  margin: 0 auto;
  padding: 32px 24px 40px;
}

/* ===== 面包屑 ===== */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  margin-bottom: 18px;
}

.bc-link {
  color: #527f77;
  cursor: pointer;
}

.bc-link:hover {
  color: #009798;
}

.bc-sep {
  color: #b6c6c1;
}

.bc-current {
  color: #304744;
  font-weight: 600;
}

/* ===== Hero ===== */
.hero-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
}

.hero-brand {
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 600;
  color: #009798;
  letter-spacing: 1px;
}

.hero-title {
  margin: 0 0 12px;
  font-size: 40px;
  font-weight: 700;
  color: #304744;
  letter-spacing: 1px;
  line-height: 1.2;
}

.hero-subtitle {
  margin: 0;
  font-size: 17px;
  color: #527f77;
}

.hero-back {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 22px;
  border: 1px solid #cfdcd8;
  border-radius: 999px;
  background: #fff;
  color: #304744;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hero-back:hover {
  border-color: #009798;
  color: #009798;
}

.hero-back-arrow {
  color: #009798;
  font-weight: 700;
}

/* ===== 步骤条 ===== */
.steps {
  display: flex;
  align-items: center;
  gap: 14px;
  list-style: none;
  margin: 0 0 28px;
  padding: 0;
}

.step {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.step-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #e6efec;
  color: #7f9994;
  font-size: 16px;
  font-weight: 700;
}

.step-text {
  font-size: 16px;
  font-weight: 500;
  color: #7f9994;
}

.step.is-active .step-dot {
  background: #009798;
  color: #fff;
  box-shadow: 0 6px 14px rgba(0, 151, 152, 0.28);
}

.step.is-active .step-text {
  color: #304744;
  font-weight: 600;
}

.step-line {
  flex: 1;
  height: 1px;
  background: #dbe6e3;
  max-width: 64px;
}

/* ===== 双栏布局 ===== */
.layout {
  display: grid;
  grid-template-columns: 1.25fr 0.95fr;
  gap: 28px;
  align-items: stretch;
}

.card {
  background: #fff;
  border: 1px solid #e9efec;
  border-radius: 22px;
  box-shadow: 0 10px 30px rgba(23, 61, 59, 0.06);
}

/* ===== 左卡：选择患者档案 ===== */
.card-patient {
  display: flex;
  flex-direction: column;
  padding: 30px 32px 26px;
}

.card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
}

.card-eyebrow {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #a9bcb6;
}

.card-title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: #304744;
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.card-count {
  font-size: 16px;
  font-weight: 500;
  color: #7f9994;
}

.add-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 42px;
  padding: 0 18px;
  border: 1.5px solid #009798;
  background: #fff;
  color: #009798;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.add-btn:hover {
  background: #eaf6f4;
}

.add-btn-plus {
  font-size: 18px;
  line-height: 1;
}

/* 搜索 */
.search-row {
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
}

.search-box {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 16px;
  border: 1px solid #dbe6e3;
  border-radius: 12px;
  background: #fbfdfd;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-box:focus-within {
  border-color: #009798;
  box-shadow: 0 0 0 3px rgba(0, 151, 152, 0.1);
}

.search-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
}

.search-btn {
  flex-shrink: 0;
  height: 46px;
  padding: 0 22px;
  border: none;
  border-radius: 12px;
  background: #009798;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.search-btn:hover {
  background: #007e7f;
}

.search-wrap {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 0;
  margin-bottom: 16px;
  overflow: hidden;
}

.search-table {
  background: transparent;
  width: 100%;
}

.search-table>>>.el-table__header-wrapper {
  width: 100%;
}

.search-table>>>.el-table__header-wrapper th {
  background: #eaf6f4;
  color: #009798;
  font-weight: 600;
  font-size: 16px;
}

.search-table>>>.el-table__row {
  font-size: 16px;
  color: #333;
  cursor: pointer;
}

.search-table>>>.el-table__row:hover td {
  background: #e3f3f1 !important;
}

.search-table>>>td,
.search-table>>>th {
  padding: 14px 0;
}

.search-table>>>.el-table__body {
  width: 100% !important;
}

.search-table>>>.el-table__empty-block {
  width: 100% !important;
}

.pagination {
  margin: 0;
  padding: 14px 0;
  text-align: center;
  background: #fff;
  font-size: 16px;
}

.pagination>>>.el-pagination.is-background .el-pager li:not(.disabled).active,
.pagination>>>.el-pagination.is-background .el-pager li.active {
  background-color: #009798 !important;
  color: #fff !important;
}

.pagination>>>.el-pagination.is-background .el-pager li:not(.disabled):hover {
  color: #009798 !important;
}

.pagination>>>.el-pagination.is-background .btn-next,
.pagination>>>.el-pagination.is-background .btn-prev,
.pagination>>>.el-pagination.is-background .el-pager li {
  background: #eaf6f4;
  color: #333;
  border-radius: 6px;
  font-size: 16px;
  min-width: 34px;
  height: 34px;
  line-height: 34px;
}

.pagination>>>.el-pagination.is-background .el-pager li:not(.disabled):hover,
.pagination>>>.el-pagination.is-background .el-pager li:hover {
  background: #009798 !important;
  color: #fff !important;
}

.pagination>>>.el-pagination.is-background .btn-next:not(:disabled):hover,
.pagination>>>.el-pagination.is-background .btn-prev:not(:disabled):hover {
  color: #009798;
  background: #e2f2ef;
}

/* 已选患者卡 */
.contact-card {
  background: #eaf6f4;
  border: 1px solid #cfe5e2;
  border-radius: 16px;
  padding: 18px 20px;
}

.cc-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 14px;
  border-bottom: 1px dashed #cfe5e2;
}

.cc-avatar {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #009798;
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cc-head-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.cc-name {
  font-size: 20px;
  font-weight: 700;
  color: #1f3a34;
}

.cc-meta {
  font-size: 16px;
  color: #527f77;
}

.cc-dot {
  margin: 0 6px;
}

.cc-tag {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 16px;
  font-weight: 600;
  color: #009798;
}

.cc-rows {
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cc-row {
  display: grid;
  grid-template-columns: 96px 1fr;
  font-size: 16px;
  line-height: 24px;
}

.cc-row .ck {
  color: #7f9994;
}

.cc-row .cv {
  color: #304744;
}

.card-hint {
  margin: 18px 0 0;
  font-size: 16px;
  color: #9aa8a3;
}

/* 左侧卡片与右侧卡片等高：提示文案沉底 */
.card-patient .card-hint {
  margin-top: auto;
  padding-top: 18px;
}

/* ===== 右卡：设置面板 ===== */
.card-setting {
  overflow: hidden;
}

.setting-head {
  padding: 24px 28px 20px;
  background: linear-gradient(135deg, #d9efeb 0%, #eef8f6 100%);
  border-bottom: 1px solid #e2efec;
}

.eyebrow-light {
  color: #6f9b93;
}

.setting-title {
  margin: 0 0 6px;
  font-size: 26px;
  font-weight: 700;
  color: #006f70;
}

.setting-sub {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #2f6d64;
}

.setting-desc {
  margin: 0;
  font-size: 16px;
  color: #527f77;
  line-height: 1.6;
}

.setting-body {
  padding: 22px 28px 26px;
}

.current-patient {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eef2f0;
}

.cp-label {
  font-size: 16px;
  font-weight: 600;
  color: #304744;
}

.cp-value {
  font-size: 16px;
  font-weight: 600;
  color: #304744;
}

.cp-sex {
  font-style: normal;
  color: #7f9994;
  font-weight: 500;
  margin-left: 6px;
}

.cp-empty {
  color: #b6c6c1;
  font-weight: 500;
}

/* 月方/日方/时方 */
.ganZhi-group {
  margin-bottom: 18px;
  display: flex;
  flex-wrap: wrap;
}

.ganZhi-group>>>.el-radio-button__inner {
  padding: 10px 24px;
  font-size: 16px;
  border-radius: 30px !important;
  background: #fff;
  color: #009798;
  border: 1px solid #009798;
  transition: all .25s;
}

.ganZhi-group>>>.el-radio-button {
  margin-right: 10px;
  margin-bottom: 8px;
}

.ganZhi-group>>>.el-radio-button__orig-radio:checked+.el-radio-button__inner {
  background: #009798;
  color: #fff;
  border-color: #009798;
  box-shadow: 0 4px 12px rgba(0, 151, 152, 0.3);
}

.prescription-tip {
  width: 100%;
  margin-top: 8px;
  font-size: 16px;
  color: #009798;
  padding-left: 4px;
}

/* 字段 */
.field-wrap {
  margin-bottom: 18px;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #304744;
}

.field {
  position: relative;
  display: flex;
  align-items: center;
  height: 46px;
  border: 1px solid #d3e6e3;
  border-radius: 12px;
  background: #fbfdfd;
  padding: 0 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.field:focus-within {
  border-color: #009798;
  box-shadow: 0 0 0 3px rgba(0, 151, 152, 0.1);
}

.field-icon {
  display: flex;
  align-items: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.field-el {
  flex: 1;
}

.field-suffix {
  flex-shrink: 0;
  margin-left: 8px;
  font-size: 16px;
  color: #7f9994;
}

/* el 组件内部去框，融入外层 field */
.field>>>.el-input__inner,
.field>>>.el-date-editor.el-input__inner,
.field>>>.el-input__inner[type="text"] {
  border: none;
  background: transparent;
  height: 44px;
  line-height: 44px;
  padding: 0;
  font-size: 16px;
  box-shadow: none;
  color: #304744;
}

.field>>>.el-input__inner::placeholder {
  color: #a7b8b4;
}

.field>>>.el-date-editor.el-input__inner {
  width: 100%;
}

.field>>>.el-input {
  width: 100%;
}

/* 快捷天数 */
.chips {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.chip {
  flex: 1;
  height: 40px;
  border: 1px solid #dbe6e3;
  border-radius: 10px;
  background: #fff;
  color: #527f77;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chip:hover {
  border-color: #009798;
  color: #009798;
}

.chip.active {
  background: #eaf6f4;
  border-color: #009798;
  color: #009798;
  font-weight: 600;
}

/* 提交按钮 */
.submit-btn {
  width: 100%;
  height: 56px;
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: none;
  border-radius: 12px;
  background: #009798;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(0, 151, 152, 0.22);
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.submit-btn:hover {
  background: #007e7f;
  box-shadow: 0 16px 30px rgba(0, 151, 152, 0.3);
}

.submit-arrow {
  font-weight: 700;
}

.setting-hint {
  margin-top: 14px;
  text-align: center;
  line-height: 1.6;
}

/* ===== 页脚 ===== */
.page-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 44px;
  padding-top: 22px;
  border-top: 1px solid #e6edea;
  font-size: 16px;
  color: #9aa8a3;
}

/* ===== 响应式：窄屏单列 ===== */
@media (max-width: 1024px) {
  .layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 20px 16px 32px;
  }

  .hero-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .hero-title {
    font-size: 28px;
  }

  .hero-back {
    height: 42px;
  }

  .steps {
    flex-wrap: wrap;
    gap: 10px;
  }

  .step-line {
    max-width: 28px;
  }

  .card-patient {
    padding: 22px 18px 20px;
  }

  .card-head {
    flex-direction: column;
  }

  .add-btn {
    align-self: flex-start;
  }

  .search-row {
    flex-direction: column;
  }

  .search-btn {
    width: 100%;
  }

  .setting-head,
  .setting-body {
    padding-left: 18px;
    padding-right: 18px;
  }

  .page-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
